import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, describeError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { PageHeader } from '../components/PageHeader'

export function ProfilePage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword
  const tooShort = newPassword.length > 0 && newPassword.length < 10

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword })
      setDone(true)
      // Changing the password revokes every session server-side, including this one.
      setTimeout(() => {
        void logout().then(() => navigate('/login', { replace: true }))
      }, 2500)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader title="Profile" subtitle="Your account, roles and effective permissions." />

      {user?.mustChangePassword && !done && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This account is flagged to change its password. Do it now - the bootstrap password was
          printed to the server log and should not stay in use.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Account
            </Typography>
            <Field label="Full name" value={user?.fullName} />
            <Field label="Username" value={user?.username} />
            <Field label="Data scope" value={user?.dataScope} />
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                Roles
              </Typography>
              {(user?.roles ?? []).map((role) => (
                <Chip key={role} label={role} size="small" sx={{ mr: 0.5, mb: 0.5, fontWeight: 600 }} />
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              {user?.permissions.length ?? 0} effective permissions, {user?.projectIds.length ?? 0}{' '}
              assigned projects, {user?.siteIds.length ?? 0} assigned sites.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Change password
            </Typography>
            {done ? (
              <Alert severity="success">
                Password changed. All sessions were revoked - signing you out.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {error && (
                  <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                    {error}
                  </Alert>
                )}
                <TextField
                  label="Current password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  fullWidth
                  required
                  autoComplete="current-password"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="New password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                  required
                  autoComplete="new-password"
                  error={tooShort}
                  helperText={tooShort ? 'At least 10 characters' : 'At least 10 characters'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Confirm new password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                  autoComplete="new-password"
                  error={mismatch}
                  helperText={mismatch ? 'Passwords do not match' : ' '}
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    submitting || !currentPassword || !newPassword || mismatch || tooShort
                  }
                >
                  {submitting ? 'Changing...' : 'Change password'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '-'}</Typography>
    </Box>
  )
}
